<template>
    <div class="ld-container" style="height: 90vh; width: 100%">
        <div class="row" style="height: 100%">
            <div class="list-area col-md-6">
                <div class="row">
                    <header-box :title="`${$t('시스템 관리')} > ${$t('플러그인 관리')}`" />
                </div>
                <!-- 플러그인 시스템 상태 바 (선택) -->
                <div class="plugin-status-bar" v-if="systemStatus">
                    <span class="status-info">
                        {{ $t("전체") }} {{ systemStatus.totalPlugins ?? 0 }}건 | {{ $t("활성") }}
                        {{ systemStatus.activePlugins ?? 0 }}건 | {{ $t("비활성") }}
                        {{ systemStatus.inactivePlugins ?? 0 }}건
                    </span>
                </div>
                <div style="height: calc(100% - 160px)">
                    <table-list
                        ref="table-list"
                        :title="$t('플러그인 목록')"
                        :columns="columns"
                        :rows="pluginList"
                        :keyField="'name'"
                        :toggleFields="['enabled']"
                        :transCodes="transCodes"
                        :controls="controls"
                        :useMaxHeight="true"
                        @button-click="handleListButtonClick"
                        :mode="'edit'"
                        @row-click="handleItemClick"
                        @toggle-changed="handleItemToggle"
                    />
                </div>
            </div>
            <div class="detail-area col-md-6">
                <PluginDetail
                    ref="PluginDetail"
                    :title="$t('플러그인 상세')"
                    :pluginName="selectedPluginName"
                    @data-updated="handleDetailUpdated"
                    @closed="handleDetailClosed"
                />
            </div>
        </div>
    </div>
</template>

<script>
import HeaderBox from "@views/component/headerBox/analysis/Header.vue";
import TableList from "@views/component/v2.1/ListDetailView/TableList.vue";
import PluginDetail from "./PluginDetail.vue";
import backEndApi from "@api/backEndApi.js";
import * as popupMessages from "@src/consts/popupMessageConsts";

export default {
    components: {
        HeaderBox,
        TableList,
        PluginDetail,
    },
    data() {
        return {
            selectedPluginName: null,
            pluginList: [],
            transCodes: [],
            systemStatus: null,
            columns: [
                {
                    label: this.$t("이름"),
                    field: "name",
                    type: "text",
                    sortable: true,
                },
                {
                    label: this.$t("버전"),
                    field: "version",
                    type: "text",
                    width: "80px",
                    sortable: false,
                },
                {
                    label: this.$t("설명"),
                    field: "description",
                    type: "text",
                    sortable: false,
                    tdClass: "left-align-wrap",
                },
                {
                    label: this.$t("사용"),
                    field: "enabled",
                    type: "text",
                    width: "80px",
                    sortable: false,
                },
            ],
            popupMessages,
        };
    },
    computed: {
        controls() {
            return [{ event: "refresh", label: this.$t("갱신"), class: "bg-white text-black" }];
        },
    },
    async mounted() {
        await this.getDataList();
        await this.loadSystemStatus();
    },
    methods: {
        async handleListButtonClick(event) {
            switch (event) {
                case "refresh":
                    await this.getDataList(event);
                    await this.loadSystemStatus();
                    break;
                default:
                    break;
            }
        },

        async handleItemClick(row, idx, toggle, check) {
            if (toggle || check) return;
            const ok = await this.$refs.PluginDetail.notifyCancelEdit();
            if (ok) {
                this.selectedPluginName = row.name;
            }
        },

        async handleItemToggle(pluginName, field, status) {
            if (field !== "enabled") return;
            const shouldActivate = status === true || status === "Y";
            try {
                if (shouldActivate) {
                    const res = await backEndApi.pluginManage.activatePlugin(pluginName);
                    if (res.status === 200 && res.data?.success !== false && !this.$err(res?.data)) {
                        this.alertNoti(this.$t("활성화되었습니다."));
                    } else {
                        this.alertWarning(res.data?.message || this.$t("활성화 실패"), "", res.data?.detail);
                        await this.getDataList();
                        return;
                    }
                } else {
                    const res = await backEndApi.pluginManage.deactivatePlugin(pluginName);
                    if (res.status === 200 && res.data?.success !== false && !this.$err(res?.data)) {
                        this.alertNoti(this.$t("비활성화되었습니다."));
                    } else {
                        this.alertWarning(res.data?.message || this.$t("비활성화 실패"), "", res.data?.detail);
                        await this.getDataList();
                        return;
                    }
                }
                await this.getDataList();
                await this.loadSystemStatus();
                if (this.selectedPluginName === pluginName) {
                    this.$refs.PluginDetail.loadData && this.$refs.PluginDetail.loadData();
                }
                this.$store.dispatch("FETCH_ACTIVE_PLUGINS");
            } catch (e) {
                console.error(e);
                this.alertWarning(this.$t("처리 중 오류가 발생했습니다."));
                await this.getDataList();
            }
        },

        async getDataList(event) {
            try {
                const result = await backEndApi.pluginManage.getPlugins();
                const body = result.data;
                if (result.status !== 200 || this.$err(body)) {
                    if (body?.message) {
                        this.alertWarning(body.message, "", typeof body.detail === "string" ? body.detail : "");
                    }
                    return;
                }
                const data = body?.data;
                const raw = Array.isArray(data?.plugins) ? data.plugins : [];
                const sorted = [...raw].sort((a, b) => (a.name || "").localeCompare(b.name || ""));
                this.pluginList = sorted.map((p) => ({
                    ...p,
                    enabled: p.status === "active" ? "Y" : "N",
                }));
                if (event) {
                    this.alertNoti(popupMessages.COMMON_REFRESH_POPUP_MESSAGE || "갱신되었습니다.");
                }
            } catch (e) {
                console.error(e);
            }
        },

        async handleDetailUpdated() {
            await this.getDataList();
            await this.loadSystemStatus();
            this.$store.dispatch("FETCH_ACTIVE_PLUGINS");
        },

        handleDetailClosed() {
            this.selectedPluginName = null;
        },

        async loadSystemStatus() {
            try {
                const result = await backEndApi.pluginManage.getSystemStatus();
                const body = result.data;
                if (result.status === 200 && body?.data) {
                    this.systemStatus = body.data;
                }
            } catch (e) {
                console.error(e);
            }
        },
    },
};
</script>

<style scoped>
.ld-container {
    height: 90vh;
    width: 100%;
    background-color: white;
    border-radius: 10px;
    overflow: hidden;
    padding: 10px 30px 10px 20px;
}

.ld-container .list-area {
    height: 100%;
    overflow: hidden;
    padding: 10px;
}

.ld-container .detail-area {
    height: 100%;
    margin-right: 0;
    padding: 0 10px;
    border-top: 1px solid #f4f4f4;
    border-right: 2px solid #e7e7e7;
    border-bottom: 2px solid #e7e7e7;
    border-left: 1px solid #f4f4f4;
    background-color: #f9f9f9;
    border-radius: 10px;
}

.plugin-status-bar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 10px;
    padding: 8px 12px;
    margin-bottom: 8px;
    background-color: #f1f1f1;
    border: 1px solid #e7e7e7;
    border-radius: 6px;
    font-size: 12px;
}

.plugin-status-bar .status-info {
    color: #666;
}
</style>
